<template>
  <div class="section-detail-container">
    <div v-if="isLoading" class="loading-state">
      <div class="loading-shimmer"></div>
    </div>

    <div v-else-if="error" class="error-message">
      <p>{{ error }}</p>
      <button @click="fetchSectionDetail" class="retry-button">Retry</button>
    </div>

    <div v-else-if="!section" class="empty-state">
      <p>Section not found.</p>
    </div>

    <div v-else class="section-content">
      <div class="section-header">
        <div class="section-type-badge" :class="section.type.toLowerCase().replace(/\s+/g, '-')">
          {{ section.type }}
        </div>
        <div class="section-status" :class="section.is_active ? 'active' : 'inactive'">
          {{ section.is_active ? 'Active' : 'Inactive' }}
        </div>
      </div>

      <h1 class="section-title">{{ section.title }}</h1>
      <p v-if="section.subtitle" class="section-subtitle">{{ section.subtitle }}</p>

      <div class="section-metadata">
        <div class="metadata-item">
          <span class="metadata-label">ID:</span>
          <span class="metadata-value">{{ section.id }}</span>
        </div>
        <div class="metadata-item">
          <span class="metadata-label">Method Name:</span>
          <span class="metadata-value">{{ section.method_name }}</span>
        </div>
        <div class="metadata-item">
          <span class="metadata-label">Cache Key:</span>
          <span class="metadata-value">{{ section.cache_key }}</span>
        </div>
        <div class="metadata-item">
          <span class="metadata-label">Display Order:</span>
          <span class="metadata-value">{{ section.order }}</span>
        </div>
      </div>

      <div class="section-stats">
        <div class="stat-card">
          <div class="stat-value">{{ section.courses_count }}</div>
          <div class="stat-label">Total Courses</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">{{ section.active_courses_count }}</div>
          <div class="stat-label">Active Courses</div>
        </div>
      </div>

      <div class="section-actions">
        <button class="edit-button" @click="editSection">
          <i class="fas fa-edit"></i> Edit Section
        </button>
        <button class="update-button" @click="updateSection">
          <i class="fas fa-sync-alt"></i> Update Section
        </button>
        <button class="toggle-button" @click="toggleSectionStatus">
          <i :class="section.is_active ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
          {{ section.is_active ? 'Deactivate' : 'Activate' }}
        </button>
        <button class="delete-button" @click="confirmDelete">
          <i class="fas fa-trash-alt"></i> Delete
        </button>
      </div>

      <!-- Delete Confirmation Modal -->
      <div v-if="showDeleteModal" class="modal-overlay">
        <div class="modal-content">
          <h3>Confirm Delete</h3>
          <p>Are you sure you want to delete the "{{ section.title }}" section? This action cannot be undone.</p>
          <div class="modal-actions">
            <button class="cancel-button" @click="showDeleteModal = false">Cancel</button>
            <button class="confirm-delete-button" @click="deleteSection">Delete</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';

export default {
  name: 'HomepageSectionDetail',
  props: {
    sectionId: {
      type: [Number, String],
      required: true
    }
  },
  emits: ['section-updated', 'section-deleted'],
  setup(props, { emit }) {
    const router = useRouter();
    const section = ref(null);
    const isLoading = ref(true);
    const error = ref(null);
    const showDeleteModal = ref(false);

    const fetchSectionDetail = async () => {
      isLoading.value = true;
      error.value = null;
      
      try {
        const token = localStorage.getItem('token') ;
        
        const response = await axios.get(`https://api.kewaar.com/api/admin/homepage-sections/${props.sectionId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
        
        if (response.data && response.data.success) {
          section.value = response.data.data;
        } else {
          error.value = 'Failed to load section details: ' + (response.data.message || 'Unknown error');
        }
      } catch (err) {
        console.error('Error fetching section details:', err);
        error.value = 'Failed to load section details: ' + (err.message || 'Network error');
      } finally {
        isLoading.value = false;
      }
    };

    const toggleSectionStatus = async () => {
      try {
        const token = localStorage.getItem('token') ;
        
        const response = await axios.patch(
          `https://api.kewaar.com/api/admin/homepage-sections/${props.sectionId}`,
          { is_active: !section.value.is_active },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          }
        );
        
        if (response.data && response.data.success) {
          section.value = response.data.data;
          emit('section-updated', section.value);
        } else {
          error.value = 'Failed to update section status: ' + (response.data.message || 'Unknown error');
        }
      } catch (err) {
        console.error('Error updating section status:', err);
        error.value = 'Failed to update section status: ' + (err.message || 'Network error');
      }
    };

    const editSection = () => {
      router.push(`/homepage/sections/${props.sectionId}/edit`);
    };

    const updateSection = () => {
      router.push(`/homepage/sections/${props.sectionId}/update`);
    };

    const confirmDelete = () => {
      showDeleteModal.value = true;
    };

    const deleteSection = async () => {
      try {
        const token = localStorage.getItem('token') ;
        
        const response = await axios.delete(
          `https://api.kewaar.com/api/admin/homepage-sections/${props.sectionId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          }
        );
        
        if (response.data && response.data.success) {
          emit('section-deleted', props.sectionId);
          router.push('/homepage/sections');
        } else {
          error.value = 'Failed to delete section: ' + (response.data.message || 'Unknown error');
          showDeleteModal.value = false;
        }
      } catch (err) {
        console.error('Error deleting section:', err);
        error.value = 'Failed to delete section: ' + (err.message || 'Network error');
        showDeleteModal.value = false;
      }
    };

    onMounted(() => {
      fetchSectionDetail();
    });

    return {
      section,
      isLoading,
      error,
      showDeleteModal,
      fetchSectionDetail,
      toggleSectionStatus,
      editSection,
      updateSection,
      confirmDelete,
      deleteSection
    };
  }
};
</script>

<style scoped>
.section-detail-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1);
}

.loading-state {
  height: 400px;
  background: #f1f5f9;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
}

.loading-shimmer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.6) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  animation: shimmer 1.5s infinite;
  transform: translateX(-100%);
}

@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}

.error-message {
  background: #FEE2E2;
  color: #B91C1C;
  padding: 1.5rem;
  border-radius: 8px;
  text-align: center;
}

.retry-button {
  background: #DC2626;
  color: white;
  border: none;
  padding: 0.5rem 1.5rem;
  border-radius: 6px;
  margin-top: 1rem;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.retry-button:hover {
  background: #B91C1C;
}

.empty-state {
  padding: 3rem;
  text-align: center;
  color: #6B7280;
  font-size: 1.1rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.section-type-badge {
  display: inline-block;
  padding: 0.35rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  background: #E5E7EB;
  color: #4B5563;
}

.section-type-badge.banner {
  background: #DBEAFE;
  color: #1E40AF;
}

.section-type-badge.fullcardcarousel {
  background: #DEF7EC;
  color: #046C4E;
}

.section-type-badge.squarecardcarouselv1,
.section-type-badge.squarecardcarouselv2 {
  background: #FEF3C7;
  color: #92400E;
}

.section-type-badge.tallcardcarousel {
  background: #FCE7F3;
  color: #9D174D;
}

.section-type-badge.gridcarousel {
  background: #E0E7FF;
  color: #3730A3;
}

.section-status {
  padding: 0.35rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.section-status.active {
  background: #D1FAE5;
  color: #065F46;
}

.section-status.inactive {
  background: #FEE2E2;
  color: #B91C1C;
}

.section-title {
  font-size: 1.8rem;
  font-weight: 700;
  color: #1F2937;
  margin: 0 0 0.5rem 0;
}

.section-subtitle {
  font-size: 1.1rem;
  color: #6B7280;
  margin: 0 0 2rem 0;
}

.section-metadata {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #F9FAFB;
  border-radius: 8px;
}

.metadata-item {
  display: flex;
  flex-direction: column;
}

.metadata-label {
  font-size: 0.75rem;
  color: #6B7280;
  margin-bottom: 0.25rem;
}

.metadata-value {
  font-size: 0.875rem;
  color: #374151;
  font-weight: 500;
  word-break: break-word;
}

.section-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 2rem;
}

.stat-card {
  padding: 1.5rem;
  background: #F3F4F6;
  border-radius: 8px;
  text-align: center;
}

.stat-value {
  font-size: 2rem;
  font-weight: 700;
  color: #1F2937;
  margin-bottom: 0.5rem;
}

.stat-label {
  font-size: 0.875rem;
  color: #6B7280;
}

.section-actions {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
}

.edit-button,
.update-button,
.toggle-button,
.delete-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.edit-button {
  background: #3B82F6;
  color: white;
  flex: 1;
}

.edit-button:hover {
  background: #2563EB;
}

.update-button {
  background: #3B82F6;
  color: white;
  flex: 1;
}

.update-button:hover {
  background: #2563EB;
}

.toggle-button {
  background: #F3F4F6;
  color: #4B5563;
  flex: 1;
}

.toggle-button:hover {
  background: #E5E7EB;
}

.delete-button {
  background: #FEE2E2;
  color: #B91C1C;
}

.delete-button:hover {
  background: #FCA5A5;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
}

.modal-content {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
}

.modal-content h3 {
  margin-top: 0;
  color: #1F2937;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
}

.cancel-button,
.confirm-delete-button {
  padding: 0.75rem 1.25rem;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.cancel-button {
  background: #F3F4F6;
  color: #4B5563;
}

.cancel-button:hover {
  background: #E5E7EB;
}

.confirm-delete-button {
  background: #DC2626;
  color: white;
}

.confirm-delete-button:hover {
  background: #B91C1C;
}

@media (max-width: 768px) {
  .section-detail-container {
    padding: 1.5rem;
  }
  
  .section-metadata {
    grid-template-columns: 1fr;
  }
  
  .section-actions {
    flex-direction: column;
  }
}
</style> 